import React, { useEffect, useState } from 'react';
import { Batch, DesignObject } from '../../types/interfaces';
import { apiFetch } from '../../utils/api';
import { API_BASE_URL } from '../../config';
import { BatchStage, parseTransferVolume, regressStageOptions, stageOptions } from '../../utils/facilityTransfer';
import { ProductOption, productChoices } from '../../utils/batchProducts';
const fmtBbl = (n: number | null | undefined) =>
  n == null || !Number.isFinite(Number(n)) ? null : String(Math.round(Number(n) * 100) / 100);

interface Props {
  batch: Batch;
  from: DesignObject;
  to: DesignObject;
  defaultStage: BatchStage;
  onCancel: () => void;
  onDone: (message: string) => void;
  /** Explicit Move back / regress stage: needs a reason and a confirm; volume optional. */
  regress?: boolean;
  /** Into a brite: offer an optional "Change product to" (e.g. Roaming Bear barrels → Barrel Aged Porter). */
  offerProductChange?: boolean;
  /** Preselect (e.g. the product already blending in the target brite). */
  suggestedProductId?: number | string | null;
}

/**
 * FV → BT (or vessel → vessel) move. Calls the same backend as Batch Details
 * "Update Equipment & Stage" with the optional transferVolume event (#127).
 * FV→BT converts Wort WIP → Brite WIP inventory at the brite location (no TTB
 * cellar / ABC writes). BT→BT relocates Brite WIP. Regress does not convert.
 */
const TransferModal: React.FC<Props> = ({ batch, from, to, defaultStage, onCancel, onDone, regress = false, offerProductChange = false, suggestedProductId = null }) => {
  const [reason, setReason] = useState('');
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [newProductId, setNewProductId] = useState(
    suggestedProductId != null && String(suggestedProductId) !== String(batch.productId) ? String(suggestedProductId) : ''
  );
  const [productReason, setProductReason] = useState('');
  const showProduct = offerProductChange && !regress;
  useEffect(() => {
    if (!showProduct) return;
    apiFetch(`${API_BASE_URL}/api/products`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((d) => setProducts(Array.isArray(d) ? d : []))
      .catch(() => setProducts([]));
  }, [showProduct]);
  const productOptions = productChoices(products, batch).filter((p) => String(p.id) !== String(batch.productId));
  const newProductName = productOptions.find((p) => String(p.id) === newProductId)?.name || null;
  const [ack, setAck] = useState(false);
  const [volume, setVolume] = useState('');
  const [stage, setStage] = useState<BatchStage>(defaultStage);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const size = fmtBbl(batch.batchSize ?? batch.volume ?? null);
  const left = fmtBbl(batch.vesselRemaining ?? null);

  const confirm = async () => {
    const parsed = regress && !volume.trim() ? { value: undefined as number | undefined } : parseTransferVolume(volume);
    if ('error' in parsed && parsed.error) return setError(parsed.error);
    if (regress && !reason.trim()) return setError('Enter a reason for moving the batch back.');
    if (regress && !ack) return setError('Tick the box to confirm moving the batch back a stage.');
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${encodeURIComponent(batch.batchId)}/equipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          equipmentId: to.equipmentId,
          stage,
          ...(parsed.value != null ? { transferVolume: parsed.value } : {}),
          ...(regress ? { regress: true, reason: reason.trim() } : {}),
          ...(showProduct && newProductId ? { productId: newProductId, productReason: productReason.trim() || undefined } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const pc = data.productChange;
      const productNote = pc?.to?.productName ? `; now ${pc.to.productName}` : pc?.error ? `; product NOT changed: ${pc.error}` : '';
      onDone(
        regress
          ? `${batch.batchId} moved back to ${to.abbreviation || to.name} (${stage})`
          : `${batch.batchId} moved to ${to.abbreviation || to.name} (${parsed.value} bbl)${productNote}`
      );
    } catch (err: any) {
      setError('Move failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="facility-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="transfer-title" data-testid="transfer-modal">
      <div className="facility-modal">
        <h3 id="transfer-title">{regress ? `Move ${batch.batchId} back` : `Transfer ${batch.batchId}`}</h3>
        {regress && (
          <p className="facility-modal__warn" data-testid="transfer-regress-warning">
            This moves the batch back to an earlier vessel/stage ({batch.stage || 'current'} → {stage}). Prefer Undo last move to reverse a Wort→Brite WIP convert. No TTB records change.
          </p>
        )}
        <p className="facility-modal__meta">
          <strong>{batch.productName || 'Product'}</strong>
          <br />
          {from.abbreviation || from.name} → <strong>{to.abbreviation || to.name}</strong>
          {(size || left) && (
            <>
              <br />
              <span data-testid="transfer-reference">
                {size ? `Batch ${size} bbl` : ''}
                {size && left ? ' · ' : ''}
                {left ? `${left} bbl left in ${from.abbreviation || from.name}` : ''}
              </span>
            </>
          )}
        </p>
        <label htmlFor="transfer-volume">Volume transferred (bbl){regress ? ' (optional)' : ''}</label>
        <input
          id="transfer-volume"
          data-testid="transfer-volume"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={volume}
          onChange={(e) => setVolume(e.target.value)}
          placeholder="e.g. 28"
          autoFocus
        />
        <label htmlFor="transfer-stage">Stage</label>
        <select id="transfer-stage" data-testid="transfer-stage" value={stage} onChange={(e) => setStage(e.target.value as BatchStage)}>
          {(regress ? regressStageOptions(batch.stage) : stageOptions(batch.stage)).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {showProduct && (
          <>
            <label htmlFor="transfer-product">Change product to (optional)</label>
            <select id="transfer-product" data-testid="transfer-product" value={newProductId} onChange={(e) => setNewProductId(e.target.value)}>
              <option value="">Keep {batch.productName || 'current product'}</option>
              {productOptions.map((p) => (
                <option key={String(p.id)} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </select>
            {newProductId && (
              <>
                <p className="facility-modal__hint" data-testid="transfer-product-note">
                  {batch.batchId} becomes <strong>{newProductName || 'the new product'}</strong>. Volume, inventory and TTB don't change; packaging will offer its package types.
                </p>
                <input
                  id="transfer-product-reason"
                  data-testid="transfer-product-reason"
                  type="text"
                  value={productReason}
                  onChange={(e) => setProductReason(e.target.value)}
                  placeholder={`Reason (optional; default "Product changed on transfer to ${to.abbreviation || to.name}")`}
                />
              </>
            )}
          </>
        )}
        {regress && (
          <>
            <label htmlFor="transfer-reason">Reason (required)</label>
            <input
              id="transfer-reason"
              data-testid="transfer-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Moved too early, needs more conditioning"
            />
            <label className="facility-modal__check">
              <input type="checkbox" data-testid="transfer-regress-confirm" checked={ack} onChange={(e) => setAck(e.target.checked)} />{' '}
              Yes, move {batch.batchId} back to {to.abbreviation || to.name}
            </label>
          </>
        )}
        {error && (
          <div className="facility-modal__error" role="alert" data-testid="transfer-error">
            {error}
          </div>
        )}
        <div className="facility-modal__actions">
          <button type="button" className="is-primary" data-testid="transfer-confirm" onClick={confirm} disabled={busy}>
            {busy ? 'Saving…' : regress ? 'Move back' : 'Confirm transfer'}
          </button>
          <button type="button" className="is-secondary" data-testid="transfer-cancel" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferModal;
